// ConfigurationsSidebar.jsx — uses options API categories for grouping
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { fmtPrice } from '../../../../contexts/InventoryContext';
import { PREDEFINED_CATEGORIES_FALLBACK } from './constants';

const T = {
  g800: '#1a4731', g700: '#1e5438', g50: '#eef7f1',
  mint: '#00c46d', mintSoft: 'rgba(0,196,109,.09)',
  ink: '#0f1f15', muted: '#506459', subtle: '#7a9585',
  border: '#e0ece4', border2: '#c8ddd2',
  surface: '#f4f8f5', white: '#ffffff',
  blue: '#2563eb', amber: '#f59e0b', violet: '#7c3aed',
};

// .cat-dot colors match HTML
const CAT_DOT = {
  residential: '#2563eb',  // var(--blue)
  commercial:  '#f59e0b',  // var(--amber)
  office:      '#7c3aed',  // var(--violet)
  warehouse:   '#455a64',
  plot:        '#00c46d',  // var(--mint)
  farmhouse:   '#5d4037',
  farm_land:   '#558b2f',
  other:       '#7a9585',
};

function groupConfigsByCategory(configs, categoryOptions) {
  const opts = categoryOptions?.length ? categoryOptions : PREDEFINED_CATEGORIES_FALLBACK;
  const order = opts.map((c) => (c.value ?? c.id ?? '').toString().toLowerCase().replace(/\s+/g, '_')).filter(Boolean);
  const byCat = {};
  (configs || []).forEach((c) => {
    const cat = (c.category || 'other').toLowerCase().replace(/\s+/g, '_');
    const key = order.includes(cat) ? cat : 'other';
    if (!byCat[key]) byCat[key] = [];
    byCat[key].push(c);
  });
  return order
    .filter((key) => byCat[key]?.length)
    .map((key) => ({
      key,
      label: opts.find((x) => (x.value ?? x.id ?? '').toString().toLowerCase().replace(/\s+/g, '_') === key)?.label || key,
      configs: byCat[key],
    }));
}

export default function ConfigurationsSidebar({ configs, selectedConfig, onSelectConfig, onAddConfig, categoryOptions }) {
  const grouped = useMemo(
    () => groupConfigsByCategory(configs, categoryOptions),
    [configs, categoryOptions]
  );

  return (
    <Box sx={{
      width: '240px', minWidth: '240px', flexShrink: 0,
      borderRight: `1px solid ${T.border}`,
      background: T.white,
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto', overflowX: 'hidden',
    }}>
      {/* .pl-header */}
      <Box sx={{
        px: '14px', py: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.8px', color: T.subtle,
        }}>
          Configurations
        </Typography>
        <Typography sx={{ fontSize: '11px', color: T.subtle }}>
          {configs?.length ?? 0} configs
        </Typography>
      </Box>

      {/* Config groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: '4px' }}>
        {grouped.map(({ key, label, configs: list }) => (
          <Box key={key}>
            {/* .cat-header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: '6px',
              px: '14px', pt: '10px', pb: '5px',
            }}>
              <Box sx={{
                width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                background: CAT_DOT[key] || T.subtle,
              }} />
              <Typography sx={{
                fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.6px', color: T.subtle,
              }}>
                {label}
              </Typography>
            </Box>

            {list.map((config) => {
              const isActive = selectedConfig?.id === config.id;
              const areaStr = (config.area_min != null || config.area_max != null)
                ? `${config.area_min ?? '?'}–${config.area_max ?? '?'} ${config.area_unit || 'sqft'}`
                : null;
              const priceStr = (config.price_min != null || config.price_max != null)
                ? `${fmtPrice(config.price_min)}–${fmtPrice(config.price_max)}`
                : null;
              const avail = config.available_units ?? config.availalble_units ?? 0;
              const meta  = [areaStr, priceStr].filter(Boolean).join(' · ');

              return (
                // .cfg-row
                <Box
                  key={config.id}
                  onClick={() => onSelectConfig(config)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    px: '14px', py: '9px',
                    cursor: 'pointer',
                    borderLeft: `3px solid ${isActive ? T.mint : 'transparent'}`,
                    background: isActive ? T.g50 : 'transparent',
                    transition: 'all 150ms ease',
                    '&:hover': { background: T.g50 },
                  }}
                >
                  {/* .cfg-row-info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: '12.5px', fontWeight: 600, color: T.ink,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {config.name}
                    </Typography>
                    {meta && (
                      <Typography sx={{
                        fontSize: '10.5px', color: T.subtle, mt: '1px',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {meta}
                      </Typography>
                    )}
                  </Box>
                  {/* right: available count */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{
                      fontFamily: "'Fraunces', 'Georgia', serif",
                      fontSize: '14px', fontWeight: 700, color: T.mint, lineHeight: 1,
                    }}>
                      {avail}
                    </Typography>
                    <Typography sx={{
                      fontSize: '9.5px', color: T.subtle,
                      textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600,
                    }}>
                      Avail
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

        {(!configs || configs.length === 0) && (
          <Box sx={{ px: '14px', py: '20px' }}>
            <Typography sx={{ fontSize: '12px', color: T.subtle, textAlign: 'center' }}>
              No configurations yet.
            </Typography>
          </Box>
        )}
      </Box>

      {/* .cfg-add-btn */}
      <Box
        onClick={onAddConfig}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          mx: '14px', my: '8px', px: '9px', py: '9px',
          borderRadius: '8px', border: `1.5px dashed ${T.border2}`,
          color: T.subtle, fontSize: '12px', fontWeight: 500,
          cursor: 'pointer', background: 'transparent',
          transition: 'all 150ms ease',
          '&:hover': { borderColor: T.mint, color: T.g700, background: T.mintSoft },
        }}
      >
        <Typography sx={{ fontSize: '16px', lineHeight: 1 }}>+</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'inherit' }}>
          Add Configuration
        </Typography>
      </Box>
    </Box>
  );
}
